import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { isValidFormInputString, isValidZipCode } from 'shared';
import { IAddressBody } from 'api/UserApi';
import { UnderlinedTextInput, Text, Colors, StateDropdown } from 'library';
import FormSection from './FormSection';

interface AddressSectionProps {
    setValid: (valid: boolean) => void;
    setAddressPatchBody: (body: IAddressBody) => void;
    expanded: boolean;
    complete: boolean;
    onToggle: () => void;
    onNext: () => void;
}

export default function AddressSection({
    setValid,
    setAddressPatchBody,
    expanded,
    complete,
    onToggle,
    onNext,
}: AddressSectionProps) {
    const [street, setStreet] = useState('');
    const [city, setCity] = useState('');
    const [zip, setZip] = useState('');
    const [state, setState] = useState<string | null>(null);

    useEffect(() => {
        const valid =
            isValidFormInputString(street ?? '') &&
            !!state &&
            isValidFormInputString(city ?? '') &&
            isValidZipCode(zip ?? '') &&
            isValidFormInputString(state ?? '');
        if (street && city && zip && state) {
            setAddressPatchBody({ street, state, city, zip_code: zip });
        }
        setValid(valid);
    }, [street, city, zip, state, setValid]);

    return (
        <FormSection
            title="Location"
            subtitle="Enter the address of your childcare center"
            icon={<LocationOnIcon />}
            expanded={expanded}
            complete={complete}
            onToggle={onToggle}
            onNext={onNext}
            autoFocusId="street"
        >
            <Grid item xs={12}>
                <UnderlinedTextInput
                    error={street !== '' && !isValidFormInputString(street)}
                    errorText={
                        street !== '' && !isValidFormInputString(street) ? 'Please enter your street address' : ''
                    }
                    id="street"
                    label="Center Street Address*"
                    value={street}
                    onChange={(value) => setStreet(value)}
                    autoComplete="street-address"
                />
            </Grid>
            <Grid item sm={6} xs={12}>
                <UnderlinedTextInput
                    error={city !== '' && !isValidFormInputString(city)}
                    errorText={city !== '' && !isValidFormInputString(city) ? 'Please enter your city' : ''}
                    id="city"
                    label="City*"
                    value={city}
                    onChange={(value) => setCity(value)}
                    autoComplete="address-level2"
                />
            </Grid>
            <Grid item sm={6} xs={12}>
                <Text variant="body2" color={Colors.darkNavy} textStyle={{ marginBottom: 4 }}>
                    State*
                </Text>
                <StateDropdown value={state} onChange={setState} />
            </Grid>
            <Grid item sm={6} xs={12}>
                <UnderlinedTextInput
                    error={zip !== '' && !isValidZipCode(zip)}
                    errorText={zip !== '' && !isValidZipCode(zip) ? 'Please enter your zip code' : ''}
                    id="zip"
                    label="Zip*"
                    value={zip}
                    onChange={(value) => setZip(value)}
                    autoComplete="postal-code"
                />
            </Grid>
        </FormSection>
    );
}

